import { ShieldCheck } from "lucide-react";
import { useEnrollment } from "@/contexts/EnrollmentContext";
import { DashboardLayout } from "@/layouts/DashboardLayout";

const WORKFLOW_STEPS = [
  {
    number: 1,
    title: "A work package lands",
    body: "Two per week: one released Monday, one Thursday, at 13:00 (UK time). Each stays open for a 3-day window.",
  },
  {
    number: 2,
    title: "Complete the work",
    body: "Use AI as you would in a real job. It's factored into the feedback model. Work Package 1 is an unweighted practice run.",
  },
  {
    number: 3,
    title: "Submit up to three times",
    body: "Each submission returns AI feedback in about 30 seconds; your final submission is the one that goes for sign-off.",
  },
  {
    number: 4,
    title: "Executive Review",
    body: "After the window closes, an experienced human reviews your work package to make sure your feedback is fair and accurate.",
  },
  {
    number: 5,
    title: "Relative Feedback",
    body: "Your percentile against the rest of the cohort, released with the Executive Review within 48 hours of the deadline.",
  },
  {
    number: 6,
    title: "Recognition",
    body: "The best contributors in each cohort earn badges, publication in UK Investor Magazine, and internship shortlists.",
  },
];

const QUANTITATIVE_BANDS = [
  { band: "60%+", meaning: "Acceptable: you did what was asked of you" },
  { band: "85%+", meaning: "Excellent: you exceeded expectations" },
  { band: "95%+", meaning: "Truly exceptional: value beyond the remit" },
];

const SUBMISSION_STEPS = [
  "Your first submission returns quantitative and qualitative AI feedback in about 30 seconds. Use it to revise.",
  "Your second submission returns AI feedback again, so you can revise once more before the window closes.",
  "Your third submission is final: it receives the Executive Review sign-off and Relative Feedback after the window closes.",
  "You receive those final results within 48 hours of the work package deadline.",
];

const BADGES = [
  {
    image: "/badges/badge-silver.png",
    alt: "Outstanding Analyst badge",
    title: "Outstanding Analyst",
    body: "Relative Feedback of 85% or higher across the cohort's work packages: the top 15% of analysts.",
    card: "border-[#DBDEE2] bg-[#F4F5F6]",
    titleColor: "text-[#6B727C]",
    bodyColor: "text-[#8A9099]",
    shadow: "shadow-[0_4px_12px_rgba(124,131,141,.3)]",
  },
  {
    image: "/badges/badge-gold.png",
    alt: "Exceptional Analyst badge",
    title: "Exceptional Analyst",
    body: "Relative Feedback of 95% or higher across the cohort's work packages: the top 5% of analysts.",
    card: "border-[#E4D3A1] bg-[#FBF6E7]",
    titleColor: "text-[#8A6D1F]",
    bodyColor: "text-[#A08544]",
    shadow: "shadow-[0_4px_12px_rgba(138,109,31,.3)]",
  },
  {
    image: "/badges/badge-published-author.png",
    alt: "Published Author badge",
    title: "Published Author",
    body: "Awarded when your work package is selected for publication in UK Investor Magazine.",
    card: "border-[#EACDC8] bg-[#FBF0EE]",
    titleColor: "text-[#A41720]",
    bodyColor: "text-[#B85B55]",
    shadow: "shadow-[0_4px_12px_rgba(164,23,32,.28)]",
  },
  {
    image: "/badges/badge-bug-buster.png",
    alt: "Bug Buster badge",
    title: "Bug Buster",
    body: "Awarded for finding and reporting a new bug in the programme via the bug button. Anyone who reports a new bug earns it.",
    card: "border-[#CBE3D2] bg-[#F0F8F2]",
    titleColor: "text-[#1E6F4F]",
    bodyColor: "text-[#5C8A75]",
    shadow: "shadow-[0_4px_12px_rgba(30,111,79,.28)]",
  },
];

const PATHWAYS = [
  {
    image: "/badges/badge-uk-investor.png",
    alt: "UK Investor Magazine",
    rounding: "rounded-[12px]",
    shadow: "shadow-[0_4px_12px_rgba(0,0,0,.18)]",
    title: "Publication",
    body: "A limited number of work packages, selected on Relative Feedback and Executive Review, are published by UK Investor Magazine. Selected contributors can cite themselves as published authors on their CV and LinkedIn, and receive a Published Author badge on their DFR portfolio.",
  },
  {
    image: "/badges/badge-internship.png",
    alt: "Internship badge",
    rounding: "rounded-full",
    shadow: "shadow-[0_4px_12px_rgba(27,47,77,.3)]",
    title: "Internships",
    body: "Some cohorts feature a sponsored internship, where a DFR customer or partner hires one of the best contributors in a subject area related to their work packages. DFR curates a shortlist using Relative Feedback and Executive Review; candidate selection and the final decision rest with the sponsoring company.",
  },
];

function SectionCard({
  label,
  title,
  accent = false,
  children,
}: {
  label: string;
  title: string;
  accent?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-[14px] border border-content-border bg-card px-7 py-[26px] ${accent ? "border-t-2 border-t-brand" : ""}`}
    >
      <div className="text-[11px] font-bold tracking-[.16em] text-content-muted">{label}</div>
      <h2 className="mt-1 text-[22px] font-bold tracking-[-.01em] text-card-foreground">{title}</h2>
      {children}
    </div>
  );
}

/** A tile in the "Three components" grid. */
function ComponentCard({
  title,
  chip,
  body,
  children,
}: {
  title: string;
  chip?: string;
  body: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2.5 rounded-xl border border-[#ECE8DA] bg-[#FDFBF4] p-[18px]">
      <div className="text-[15px] font-bold text-card-foreground">{title}</div>
      {chip && (
        <span className="w-fit rounded-md border border-[#E4D3A1] bg-[#FBF6E7] px-2 py-1 text-[9.5px] font-bold tracking-[.1em] text-[#8A6D1F]">
          {chip}
        </span>
      )}
      <div className="text-[12.5px] leading-[1.55] text-[#5C6E63]">{body}</div>
      {children}
    </div>
  );
}

export default function HowTheProgrammeWorksPage() {
  const { enrollment } = useEnrollment();
  const cohortName = enrollment?.programme?.name;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto flex flex-col gap-[26px]">
        {/* Header */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[.16em] text-content-muted">
            The programme
          </p>
          <h1 className="mt-1.5 text-3xl sm:text-4xl font-extrabold tracking-[-.015em] text-content-foreground">
            How the programme works
          </h1>
          <p className="mt-1.5 max-w-2xl text-[15px] text-[#5C6E63]">
            The DFR programme is not an academic course: it is an accurate simulation of real-world
            work experience. You complete work packages, receive feedback the way you would from a
            good manager, and the best contributors earn public recognition.
          </p>
        </div>

        {/* Workflow */}
        <SectionCard label="WORKFLOW" title="From brief to recognition" accent>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {WORKFLOW_STEPS.map((step) => (
              <div
                key={step.number}
                className="flex flex-col gap-2.5 rounded-xl border border-[#ECE8DA] bg-[#FDFBF4] p-[18px]"
              >
                <span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {step.number}
                </span>
                <div className="text-sm font-bold leading-[1.3] text-card-foreground">
                  {step.title}
                </div>
                <div className="text-[12.5px] leading-[1.55] text-[#5C6E63]">{step.body}</div>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Feedback components */}
        <SectionCard label="FEEDBACK" title="Three components, like real-world employment">
          <p className="mt-1.5 max-w-[680px] text-sm text-[#5C6E63]">
            When you submit a work package you get feedback on it, just as you would in a real job.
          </p>
          <div className="mt-[18px] grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <ComponentCard
              title="Quantitative"
              body="How well you completed the work package: your understanding of the brief and your skill in completing it well. Expressed as a percentage."
            >
              <div className="mt-0.5 flex flex-col gap-2">
                {QUANTITATIVE_BANDS.map((b) => (
                  <div key={b.band} className="flex gap-2.5 text-[12.5px] leading-[1.5]">
                    <span className="flex-none font-bold text-[#1E6F4F]">{b.band}</span>
                    <span className="text-[#5C6E63]">{b.meaning}</span>
                  </div>
                ))}
              </div>
            </ComponentCard>
            <ComponentCard
              title="Qualitative"
              body="Why you received that percentage. Like any good manager, it notes what you did well and gives concrete guidance on how to improve your performance going forward."
            />
            <ComponentCard
              title="Relative"
              chip={cohortName ? `FROM THE ${cohortName.toUpperCase()}` : "FROM YOUR COHORT"}
              body="Your performance relative to the other members of your cohort, expressed as a percentile. In a real-world job, relative performance plays a large role in what gets you noticed and promoted."
            />
          </div>
        </SectionCard>

        {/* Executive Review */}
        <div className="rounded-[14px] border border-content-border bg-card px-7 py-[26px]">
          <div className="text-[11px] font-bold tracking-[.16em] text-content-muted">
            EXECUTIVE REVIEW
          </div>
          <div className="mt-1 flex items-center gap-3.5">
            <div className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-[11px] bg-[#E9F1E6]">
              <ShieldCheck className="h-[19px] w-[19px] text-[#1E6F4F]" strokeWidth={1.7} />
            </div>
            <h2 className="text-[22px] font-bold tracking-[-.01em] text-card-foreground">
              A human signs off every work package
            </h2>
          </div>
          <p className="mt-3.5 text-sm leading-[1.65] text-[#5C6E63]">
            We use AI for quantitative and qualitative feedback. It lets you evaluate your
            performance in real time rather than waiting weeks for results, and our initial tests
            indicate it provides a more accurate, objective and unbiased assessment than most human
            evaluators. But AI isn't perfect: some errors are subtle and nuanced, some are silly,
            and some can be catastrophic. To protect you from AI errors, every work package you
            submit receives a final review by qualified people with years of experience in their
            knowledge domains, ensuring your feedback is both fair and accurate.
          </p>
        </div>

        {/* Repeat submissions */}
        <SectionCard label="REPEAT SUBMISSIONS" title="Three submissions per work package">
          <p className="mt-1.5 max-w-[680px] text-sm leading-[1.65] text-[#5C6E63]">
            In the real world, when a boss assigns you a work package, you would almost certainly
            use AI to improve and refine your work. We expect you to, and it will be considerably
            harder to receive a high percentile if you can't use AI well. To simulate that, each
            work package can be submitted up to three times.
          </p>
          <div className="mt-[18px] flex flex-col">
            {SUBMISSION_STEPS.map((text, i) => (
              <div
                key={text}
                className="flex gap-4 border-b border-content-border/60 px-1 py-[13px]"
              >
                <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full border-[1.5px] border-brand text-[11.5px] font-bold text-[#1E6F4F]">
                  {i + 1}
                </span>
                <div className="pt-px text-sm leading-[1.6] text-[#28402F]">{text}</div>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Recognition */}
        <SectionCard label="RECOGNITION" title="Badges, publication and internships" accent>
          <p className="mt-1.5 max-w-[680px] text-sm text-[#5C6E63]">
            In each cohort, DFR selects a limited number of the best contributors for public
            recognition.
          </p>
          <div className="mt-[18px] grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            {BADGES.map((badge) => (
              <div
                key={badge.title}
                className={`flex items-start gap-3.5 rounded-xl border p-[18px] ${badge.card}`}
              >
                <img
                  src={badge.image}
                  alt={badge.alt}
                  className={`h-[54px] w-[54px] flex-none rounded-full object-cover ${badge.shadow}`}
                />
                <div className="min-w-0">
                  <div className={`text-[15px] font-bold tracking-[-.01em] ${badge.titleColor}`}>
                    {badge.title}
                  </div>
                  <div className={`mt-1 text-[12.5px] leading-[1.55] ${badge.bodyColor}`}>
                    {badge.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3.5 flex items-center gap-2.5">
            <span className="h-px w-[26px] flex-none bg-content-border" />
            <span className="font-serif text-[12.5px] italic text-content-muted">
              badges appear on your public portfolio, with a secured digital copy for your CV or
              LinkedIn, each with a hash value to guarantee authenticity
            </span>
          </div>
          <div className="mt-2 flex flex-col">
            {PATHWAYS.map((pathway) => (
              <div
                key={pathway.title}
                className="flex items-start gap-4 border-b border-content-border/60 px-1 py-[18px] last:border-b-0"
              >
                <img
                  src={pathway.image}
                  alt={pathway.alt}
                  className={`h-[54px] w-[54px] flex-none object-cover ${pathway.rounding} ${pathway.shadow}`}
                />
                <div className="min-w-0">
                  <div className="text-[14.5px] font-bold text-card-foreground">
                    {pathway.title}
                  </div>
                  <div className="mt-1 text-[13px] leading-[1.6] text-[#5C6E63]">
                    {pathway.body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </DashboardLayout>
  );
}
